import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useRef } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { Turnstile } from "@/components/ui/turnstile";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  captchaToken: z.string().min(1, "Please complete the verification challenge"),
});

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSendingIssue, setEmailSendingIssue] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
      captchaToken: "",
    },
  });

  const onCaptchaVerify = (token: string) => {
    form.setValue("captchaToken", token);
  };

  const onCaptchaExpire = () => {
    form.setValue("captchaToken", "");
    toast({
      title: "Verification Expired",
      description: "Please complete the verification again before submitting.",
      variant: "destructive",
    });
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setEmailSendingIssue(false);
    setSubmissionError(null);
    
    try {
      // Verify captchaToken exists
      if (!values.captchaToken) {
        throw new Error("Please complete the verification challenge");
      }

      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          message: values.message,
          captchaToken: values.captchaToken,
        },
      });

      if (error) {
        console.error("Function error:", error);
        setSubmissionError("There was an error sending your message. Please try again later.");
        throw error;
      }

      // Check if there were any issues with sending emails
      if (data && (!data.adminEmailSent || !data.userEmailSent)) {
        console.warn("Email sending issues:", data);
        setEmailSendingIssue(true);
        
        toast({
          title: "Message submitted successfully!",
          description: "Your message was received, but there may have been an issue with the email confirmation.",
        });
      } else {
        toast({
          title: "Message sent!",
          description: "We'll get back to you as soon as possible.",
        });
      }
      
      form.reset();
      
      // The reset happens automatically by remounting the component
      // No need to explicitly reset the turnstile widget
    } catch (error) {
      console.error("Error sending message:", error);
      
      if (!submissionError) {
        setSubmissionError("There was an error sending your message. Please try again later.");
      }
      
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow bg-gradient-to-b from-muted to-white">
        <div className="container mx-auto px-4 pt-32 pb-16">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-2">Contact Us</h1>
            <p className="text-muted-foreground text-center mb-8">
              Drop us a line if you have any questions
            </p>

            {emailSendingIssue && (
              <Alert className="mb-6">
                <Info className="h-4 w-4" />
                <AlertTitle>Note</AlertTitle>
                <AlertDescription>
                  Your message was submitted successfully, but there may have been an issue sending the confirmation email. 
                  We've still received your message and will contact you soon.
                </AlertDescription>
              </Alert>
            )}

            {submissionError && (
              <Alert className="mb-6" variant="destructive">
                <Info className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email*</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="example@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Your message here..."
                          className="min-h-[150px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="captchaToken"
                  render={() => (
                    <FormItem>
                      <Turnstile 
                        onVerify={onCaptchaVerify}
                        onExpire={onCaptchaExpire}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
